import type { CSSProperties } from 'react';

import styles from './skeleton.module.scss';

export type SkeletonBlockProps = {
  /** Width — number (px) or any CSS length. Defaults to 100%. */
  width?: number | string;
  /** Height — number (px) or any CSS length. Defaults to 16px. */
  height?: number | string;
  /** Border radius override. Ignored when `circle` is set. */
  radius?: number | string;
  /** Render a circle (uses `height` as the diameter). */
  circle?: boolean;
  className?: string;
  style?: CSSProperties;
};

/**
 * The shared shimmer building block. Every module-specific skeleton composes this
 * (instead of antd `Skeleton` or flat muted boxes) so the shimmer animation is identical
 * everywhere. Decorative by design — the surrounding skeleton container owns `aria-busy`.
 */
export function SkeletonBlock({
  width = '100%',
  height = 16,
  radius,
  circle = false,
  className,
  style,
}: SkeletonBlockProps) {
  return (
    <span
      aria-hidden="true"
      className={[styles.block, className].filter(Boolean).join(' ')}
      style={{
        width: circle ? height : width,
        height,
        borderRadius: circle ? '50%' : radius,
        flexShrink: 0,
        ...style,
      }}
    />
  );
}

export type SkeletonTextProps = {
  /** Number of lines. Defaults to 3. */
  lines?: number;
  /** Height of each line. Defaults to 12px. */
  lineHeight?: number | string;
  /** Width of the final (shortened) line. Defaults to 60%. */
  lastLineWidth?: number | string;
  className?: string;
};

/** Convenience stack of shimmer lines for paragraph-shaped placeholders. */
export function SkeletonText({
  lines = 3,
  lineHeight = 12,
  lastLineWidth = '60%',
  className,
}: SkeletonTextProps) {
  return (
    <span className={[styles.text, className].filter(Boolean).join(' ')} aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <SkeletonBlock
          key={i}
          height={lineHeight}
          width={i === lines - 1 ? lastLineWidth : '100%'}
        />
      ))}
    </span>
  );
}
